import { useMemo } from 'react'
import { uniqBy } from 'lodash-es'

import type { RouterOutputs } from '@/utils/api'
import type { VoiceCategory, VoiceItem } from '../../../_types/form'

import { api } from '@/utils/api'
import { useLocale } from '@/hooks/routes'

type VoicePreviewAudioOutput = RouterOutputs['tts']['getVoicePreviewAudio']

/**
 * 将后端 getVoicePreviewAudio 返回的数据映射为前端 VoiceCategory / VoiceItem 结构。
 */
function mapApiCategories(apiData: VoicePreviewAudioOutput): VoiceCategory[] {
  return apiData.map((cat) => ({
    key: cat.category_name,
    displayName: cat.category_name,
    voices: cat.voice_list.map((v) => ({
      id: v.voice_id,
      name: v.name,
      category: v.category,
      categoryGroup: cat.category_name,
      coverUrl: v.cover || '',
      previewUrl: v.preview_url,
    })),
  }))
}

export function useVoices() {
  const locale = useLocale()
  const { data, isLoading } = api.tts.getVoicePreviewAudio.useQuery(
    { language: locale || 'en' },
    { staleTime: 5 * 60 * 1000 },
  )

  const categories: VoiceCategory[] = useMemo(
    () => (data ? mapApiCategories(data) : []),
    [data],
  )

  const allVoices: VoiceItem[] = useMemo(
    () => categories.flatMap((c) => c.voices),
    [categories],
  )

  const searchVoices = (keyword: string): VoiceItem[] => {
    const lower = keyword.toLowerCase()
    return uniqBy(
      allVoices.filter((v) => v.name.toLowerCase().includes(lower)),
      'id',
    )
  }

  return {
    categories,
    allVoices,
    isLoading,
    searchVoices,
  }
}
